/**
 * ReportHeader - Hero header with dynamic workshop metadata.
 * Reads title, date, location from instance info; participant count from pathways.
 */
import { useState } from 'react';
import styled from '@emotion/styled';
import { Printer, X, Pencil, Check } from 'lucide-react';
import ModalWrapper from '../ModalWrapper';
import { useReportEdits } from './ReportEditsContext';
import EditableText from './EditableText';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { dateLocale } from '@kuutio/shared/reportLanguages.js';

// Sitra brand: pure black hero with white text + yellow accent line.
// Iterating from black → can switch to white-on-black if we go fuller-Sitra later.
const Header = styled.header`
  background: #000;
  color: #fff;
  padding: 80px 32px 48px;
  font-family: 'Rational', 'Inter', -apple-system, sans-serif;
  position: relative;

  /* Sitra yellow accent — top stripe */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 8px;
    background: #FFD93D;
  }

  @media print {
    background: #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    padding: 24px 0;
  }
`;

const HeaderContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  @media print { max-width: 100%; }
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  @media print { display: none; }
`;

// Sitra | Kuutio attribution lockup. The Sitra wordmark is the
// /assets/sitra_logo_musta.svg asset rendered white via filter (the SVG
// itself is black), sized to roughly match KUUTIO N's visual height.
const Logo = styled.div`
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;

  .sitra-img {
    height: 16px;
    width: auto;
    /* SVG is black ("musta"); brightness(0) + invert(1) → solid white,
     * preserving anti-aliased edges. */
    filter: brightness(0) invert(1);
    display: block;
  }
  .sep {
    color: rgba(255, 255, 255, 0.3);
    font-weight: 400;
    font-size: 16px;
  }
  .kuutio {
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
  }

  @media (max-width: 600px) {
    .sitra-img { height: 14px; }
    .sep       { font-size: 14px; }
  }
`;

const Title = styled.h1`
  font-size: 44px;
  font-weight: 800;
  margin: 0 0 16px 0;
  line-height: 1.1;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.75);
  margin: 0;
  line-height: 1.5;
  max-width: 720px;
`;

const MetaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  > * { flex: 1 1 150px; min-width: 0; }
`;

const MetaItem = styled.div`
  font-size: 14px;
`;

const MetaLabel = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 4px;
`;

const MetaValue = styled.div`
  font-weight: 600;
`;

const OrgLink = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  padding: 4px 0 0;
  &:hover { color: #fff; }
  @media print { display: none; }
`;

const PrintOrgList = styled.div`
  display: none;
  @media print {
    display: block;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 4px;
    line-height: 1.5;
  }
`;

const OrgModalBox = styled.div`
  background: #fff;
  border-radius: 0;
  padding: 0 28px 24px;
  max-width: 640px;
  width: 92%;
  max-height: 82vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const OrgItem = styled.div`
  padding: 14px 0;
  border-bottom: 1px solid #f3f4f6;
  &:last-child { border-bottom: none; }
`;

const OrgName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #000;
  margin-bottom: 4px;
`;

const OrgDesc = styled.div`
  font-size: 13px;
  color: #4b5563;
  line-height: 1.55;
`;

const OrgFocus = styled.div`
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const FocusChip = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 0;
  background: #f5f5f5;
  color: #2c5e91;
`;

const PrintButton = styled.button`
  background: #FFD93D;
  color: #000;
  border: 2px solid #FFD93D;
  border-radius: 4px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.15s ease;
  letter-spacing: 0.02em;

  &:hover {
    background: #fff;
    border-color: #fff;
  }
  @media print { display: none; }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  @media print { display: none; }
`;

const EditToggle = styled.button`
  background: ${p => (p.$active ? '#7c3aed' : 'transparent')};
  color: ${p => (p.$active ? '#fff' : '#fff')};
  border: 2px solid ${p => (p.$active ? '#7c3aed' : 'rgba(255,255,255,0.4)')};
  border-radius: 4px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.15s ease;
  letter-spacing: 0.02em;
  &:hover {
    background: ${p => (p.$active ? '#5b21b6' : 'rgba(255,255,255,0.08)')};
    border-color: ${p => (p.$active ? '#5b21b6' : 'rgba(255,255,255,0.7)')};
  }
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 0;
  padding: 28px 32px;
  max-width: 440px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #000;
  /* Sticky title bar — close button stays visible while the org list
   * scrolls underneath. Padding moved here from OrgModalBox so the
   * sticky region has its own background fill. */
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
  margin: 0;
  padding: 20px 0 16px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CloseBtn = styled.button`
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  color: #374151;
  cursor: pointer;
  padding: 6px;
  border-radius: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;

  svg {
    stroke: currentColor;
    color: #374151;
  }

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }
`;

const OptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #367ABA;
  cursor: pointer;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const ModalBtn = styled.button`
  padding: 10px 20px;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${p => p.$primary ? 'transparent' : '#e5e7eb'};
  background: ${p => p.$primary ? '#000' : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#374151'};
  &:hover { opacity: 0.9; }
`;

function formatWorkshopDate(instanceInfo, locale = 'fi-FI') {
  // Report stamp = last curator edit (or generated_at). Do not use workshop_date
  // or instance created_at — those are unrelated to "when was this report updated".
  const raw = instanceInfo?.last_edited_at;
  if (!raw) return null;
  return new Date(raw).toLocaleDateString(locale, {
    day: 'numeric', month: 'numeric', year: 'numeric'
  });
}

export default function ReportHeader({ instanceInfo, participantCount, submissionCount, solutionCount = 0, organisations = [], onPrintOptionsChange: _onPrintOptionsChange }) {
  // Print state + modal moved to PublicWorkshopReport so the top-bar button
  // can trigger it from outside this component tree.
  const [showOrgModal, setShowOrgModal] = useState(false);
  const { t, language } = useTranslation();

  const title = instanceInfo?.title || instanceInfo?.name || 'Kuutio-työpaja';
  const date = formatWorkshopDate(instanceInfo, dateLocale(language));
  const location = instanceInfo?.metadata?.workshop_location || null;

  return (
    <Header id="header">
      {showOrgModal && (
        <ModalWrapper visible={showOrgModal} onClose={() => setShowOrgModal(false)} showCloseButton={false} opacity={0.5} blur={0}>
          <OrgModalBox>
            <ModalTitle>
              Osallistuvat organisaatiot ({organisations.length})
              <CloseBtn onClick={() => setShowOrgModal(false)}><X size={18} /></CloseBtn>
            </ModalTitle>
            {organisations.map(o => (
              <OrgItem key={o.id}>
                <OrgName>{o.name}</OrgName>
                {o.description && <OrgDesc>{o.description}</OrgDesc>}
                {Array.isArray(o.focus_areas) && o.focus_areas.length > 0 && (
                  <OrgFocus>
                    {o.focus_areas.map((f, i) => <FocusChip key={i}>{f}</FocusChip>)}
                  </OrgFocus>
                )}
              </OrgItem>
            ))}
          </OrgModalBox>
        </ModalWrapper>
      )}
      {/* Print modal lives in PublicWorkshopReport now. */}
      <HeaderContent>
        <HeaderActions>
          <Logo>
            <img className="sitra-img" src="/assets/sitra_logo_musta.svg" alt="Sitra" />
            <span className="sep">/</span>
            <span className="kuutio">KUUTIO{instanceInfo?.metadata?.kuutio_number ? ` ${instanceInfo.metadata.kuutio_number}` : ''}</span>
          </Logo>
          {/* Edit + print buttons moved to the top bar (MainViewHeader
              children → TopBarReportActions). Hero stays clean. */}
        </HeaderActions>
        <Title>
          <EditableText fieldKey="header.title" fallback={title} multiline={false} />
        </Title>
        <Subtitle>
          <EditableText
            fieldKey="header.subtitle"
            fallback={t('report.header.subtitle')}
            multiline={false}
          />
        </Subtitle>

        <MetaGrid>
          {date && (
            <MetaItem>
              <MetaLabel>{t('report.header.date')}</MetaLabel>
              <MetaValue>{date}</MetaValue>
            </MetaItem>
          )}
          {location && (
            <MetaItem>
              <MetaLabel>{t('report.header.location')}</MetaLabel>
              <MetaValue>{location}</MetaValue>
            </MetaItem>
          )}
          {participantCount > 0 && (
            <MetaItem>
              <MetaLabel>{t('report.header.participants')}</MetaLabel>
              <MetaValue>{participantCount}</MetaValue>
              {organisations.length > 0 && (
                <>
                  <OrgLink onClick={() => setShowOrgModal(true)}>
                    {t('report.header.organisationsCount', { count: organisations.length })}
                  </OrgLink>
                  <PrintOrgList>
                    {organisations.map(o => o.name).filter(Boolean).join(' · ')}
                  </PrintOrgList>
                </>
              )}
            </MetaItem>
          )}
          {solutionCount > 0 && (
            <MetaItem>
              <MetaLabel>{t('report.header.solutions')}</MetaLabel>
              <MetaValue>{solutionCount}</MetaValue>
            </MetaItem>
          )}
        </MetaGrid>
      </HeaderContent>
    </Header>
  );
}
